import React from 'react';
import Icon, { IconProps } from './Icon';

export interface SpeakingFaceIconProps extends IconProps {
  /**
   * Whether the face should be speaking (lips animated)
   */
  isSpeaking?: boolean;
  /**
   * Animation duration in milliseconds
   */
  animationDuration?: number;
}

/**
 * Gender-agnostic speaking face icon with animated lips
 * Lips animate when isSpeaking is true, closed when false
 */
export const SpeakingFaceIcon: React.FC<SpeakingFaceIconProps> = ({
  isSpeaking = false,
  animationDuration = 800,
  size = 24,
  color = "currentColor",
  className,
  ...props
}) => {
  return (
    <Icon
      size={size}
      color={color}
      className={className}
      viewBox="0 0 24 24"
      {...props}
    >
      {/* Face outline - gender agnostic, simple and clean */}
      <circle 
        cx="12" 
        cy="12" 
        r="10" 
        fill="none" 
        stroke={color}
        strokeWidth="1.5"
      />
      
      {/* Eyes - more realistic with proper spacing */}
      <ellipse cx="8" cy="11" rx="1.2" ry="1.8" fill={color} />
      <ellipse cx="16" cy="11" rx="1.2" ry="1.8" fill={color} />
      {/* Pupils for more realistic eyes */}
      <circle cx="8" cy="11" r="0.6" fill="white" />
      <circle cx="16" cy="11" r="0.6" fill="white" />
      
      {/* Eyebrows - better positioned with proper spacing from eyes */}
      <path 
        d="M 6 7.5 Q 8 6.5 10 7.5" 
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      <path 
        d="M 18 7.5 Q 16 6.5 14 7.5" 
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      
      {/* Nose - simple, gender neutral */}
      <path 
        d="M 12 12 L 12 14" 
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      
      {/* Animated mouth/lips */}
      {isSpeaking ? (
        // Speaking state - animated open mouth
        <g>
          {/* Animated mouth shape */}
          <ellipse 
            cx="12" 
            cy="17" 
            rx="2.5" 
            ry="1.5"
            fill={color}
            opacity="0.8"
          >
            <animate
              attributeName="ry"
              values="1.5;2.5;1.5;0.8;1.5"
              dur={`${animationDuration}ms`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="rx"
              values="2.5;2;2.5;3;2.5"
              dur={`${animationDuration}ms`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.8;1;0.7;0.9;0.8"
              dur={`${animationDuration}ms`}
              repeatCount="indefinite"
            />
          </ellipse>
          
          {/* Lip movement lines */}
          <path 
            d="M 9 17 Q 12 15 15 17" 
            stroke={color}
            strokeWidth="1"
            fill="none"
            opacity="0.6"
          >
            <animate
              attributeName="d"
              values="M 9 17 Q 12 15 15 17;M 9 17 Q 12 14 15 17;M 9 17 Q 12 16 15 17;M 9 17 Q 12 15 15 17"
              dur={`${animationDuration}ms`}
              repeatCount="indefinite"
            />
          </path>
        </g>
      ) : (
        // Silent state - closed mouth
        <path 
          d="M 9 17 Q 12 19 15 17" 
          stroke={color}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
      )}
    </Icon>
  );
};

export default SpeakingFaceIcon;
